import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Building2, CreditCard, FileText, LogOut, PoundSterling, Ticket, Plus, Trash2, Newspaper, LogIn, GripVertical, CheckCircle2, Users, ShieldCheck, Zap, ChevronUp, ChevronDown, Gem, KeyRound, History } from "lucide-react";
import { ResetPasswordDialog } from "./admin/ResetPasswordDialog";
import PlansAdmin from "./admin/PlansAdmin";
import ActivityLog from "./admin/ActivityLog";
import { StripeStatusPanel } from "./admin/StripeStatus";
import { useWebhookHealth, WebhookAlert, WebhookHealthPanel } from "./admin/WebhookHealth";
import { toast } from "../hooks/use-toast";
import ImageUpload from "../components/ImageUpload";

const CONTENT_GROUPS = [
  { title: "SEO & page title", keys: ["meta_title", "meta_description"] },
  { title: "Branding & logo", keys: ["site_logo", "login_hero_image", "favicon", "og_image"] },
  { title: "Hero", keys: ["hero_title", "hero_subtitle", "hero_cta", "hero_image_envelope", "hero_image_phone"] },
  { title: "Voucher stores", keys: ["stores_title", "stores_subtitle", "stores_cta"] },
  { title: "Video messages", keys: ["video_title", "video_text", "video_cta", "video_image"] },
  { title: "Admin tools", keys: ["admin_title", "admin_text", "admin_cta", "admin_quote", "admin_quote_name", "admin_quote_role", "admin_quote_avatar"] },
  { title: "Fulfilment", keys: ["fulfil_title", "fulfil_text", "fulfil_cta", "fulfil_image_1", "fulfil_image_2", "fulfil_image_3"] },
  { title: "Support", keys: ["support_title", "support_text", "support_cta", "support_quote", "support_quote_name", "support_quote_role", "support_quote_avatar"] },
  { title: "Integrations", keys: ["integ_title", "integ_text", "integ_cta"] },
  { title: "Grow CTA", keys: ["grow_title", "grow_subtitle"] },
  { title: "Blog", keys: ["blog_title"] },
  { title: "Footer", keys: ["footer_tagline"] },
];

export default function AdminPanel() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("businesses");
  const [businesses, setBusinesses] = useState(null);
  const webhookHealth = useWebhookHealth();
  const [platform, setPlatform] = useState(null);
  const [fees, setFees] = useState(null);
  const [revFilter, setRevFilter] = useState("");
  const [payouts, setPayouts] = useState(null);
  const [content, setContent] = useState(null);
  const [articles, setArticles] = useState(null);
  const [planList, setPlanList] = useState([]);
  const [smsTo, setSmsTo] = useState("");
  const [pwBiz, setPwBiz] = useState(null);

  useEffect(() => {
    if (user?.role === "super_admin") {
      const fullAdmin = user.staff_role !== "business_manager";
      api.get("/admin/businesses").then(({ data }) => setBusinesses(data));
      api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
      api.get("/admin/payouts").then(({ data }) => setPayouts(data)).catch(() => {});
      api.get("/site-content").then(({ data }) => setContent(data));
      api.get("/articles").then(({ data }) => setArticles(data));
      api.get("/plans").then(({ data }) => setPlanList(data.plans)).catch(() => {});
      if (fullAdmin) {
        api.get("/admin/platform").then(({ data }) => setPlatform(data)).catch(() => {});
      }
    }
  }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  const isFullAdmin = user.staff_role !== "business_manager";

  const savePlatform = async () => {
    await api.put("/admin/platform", platform);
    toast({ title: "Platform settings saved" });
  };
  const saveContent = async () => {
    await api.put("/site-content", { content });
    toast({ title: "Site content updated", description: "Marketing page reflects your edits." });
  };
  const loginAs = async (bid) => {
    try {
      const { data } = await api.post(`/admin/impersonate/${bid}`);
      localStorage.setItem("gp_token", data.token);
      window.location.href = "/dashboard";
    } catch (err) {
      toast({ title: "Error", description: "Could not log in as business", variant: "destructive" });
    }
  };
  const testSms = async () => {
    try {
      const { data } = await api.post("/admin/sms-test", { to: smsTo });
      toast({ title: "Test SMS sent", description: `Sent to ${data.to}` });
    } catch (err) {
      toast({ title: "SMS failed", description: err?.response?.data?.detail || "Check your Twilio settings", variant: "destructive" });
    }
  };
  const assignPlan = async (b, plan_id) => {
    try {
      await api.put(`/admin/businesses/${b.id}/plan`, { plan_id });
      toast({ title: `${b.name} moved to ${planList.find((p) => p.id === plan_id)?.name || plan_id}` });
      reloadBiz();
    } catch (err) {
      toast({ title: "Could not change plan", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };
  const reloadBiz = () => {
    api.get("/admin/businesses").then(({ data }) => setBusinesses(data));
    api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
  };
  const setSubStatus = async (b, status) => {
    try {
      await api.put(`/admin/businesses/${b.id}/subscription`, { status });
      toast({ title: "Subscription updated", description: `${b.name} set to ${status}.` });
      api.get("/admin/fees").then(({ data }) => setFees(data)).catch(() => {});
    } catch { toast({ title: "Error", description: "Could not update subscription", variant: "destructive" }); }
  };
  const markPayoutPaid = async (r) => {
    if (!window.confirm(`Mark £${r.amount.toFixed(2)} payout to ${r.business_name} as PAID? Do this only after you've sent the money.`)) return;
    try {
      await api.post(`/admin/payouts/${r.id}/mark-paid`);
      toast({ title: "Payout marked paid" });
      api.get("/admin/payouts").then(({ data }) => setPayouts(data)).catch(() => {});
    } catch { toast({ title: "Error", description: "Could not update payout", variant: "destructive" }); }
  };
  const toggleSuspend = async (b) => {
    let reason = "";
    if (!b.suspended) {
      reason = window.prompt(`Suspend ${b.name}? Their login and storefront go offline (data is kept). Optional reason for your records:`, "");
      if (reason === null) return;
    }
    try {
      await api.put(`/admin/businesses/${b.id}/suspend`, { suspended: !b.suspended, reason });
      toast({ title: b.suspended ? `${b.name} reactivated` : `${b.name} suspended` });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not update", variant: "destructive" }); }
  };
  const archiveBiz = async (b) => {
    try {
      const { data } = await api.post(`/admin/businesses/${b.id}/archive`);
      toast({ title: data.archived ? "Business archived" : "Business restored" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not archive", variant: "destructive" }); }
  };
  const deleteBiz = async (b) => {
    if (!window.confirm(`Permanently delete "${b.name}" and all its vouchers? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/businesses/${b.id}`);
      toast({ title: "Business deleted" });
      reloadBiz();
    } catch { toast({ title: "Error", description: "Could not delete", variant: "destructive" }); }
  };
  const runReminders = async () => {
    try {
      const { data } = await api.post("/admin/run-expiry-reminders");
      toast({ title: "Expiry reminders run", description: `Sent ${data.sent}, skipped ${data.skipped} (checked ${data.checked}).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run reminders", variant: "destructive" });
    }
  };
  const runScheduledSends = async () => {
    try {
      const { data } = await api.post("/admin/run-scheduled-sends");
      toast({ title: "Scheduled sends run", description: `Sent ${data.sent} of ${data.scheduled_total} scheduled voucher(s).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run scheduled sends", variant: "destructive" });
    }
  };
  const runCartNudges = async () => {
    try {
      const { data } = await api.post("/admin/run-cart-nudges?min_age_minutes=0");
      toast({ title: "Cart nudges run", description: data.reason ? data.reason : `Sent ${data.sent}, skipped ${data.skipped} (of ${data.candidates} pending).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run cart nudges", variant: "destructive" });
    }
  };

  const runJob = async (url, title, describe) => {
    try {
      const { data } = await api.post(url);
      toast({ title, description: describe(data) });
    } catch (err) {
      toast({ title: "Error", description: `Could not run ${title.toLowerCase()}`, variant: "destructive" });
    }
  };

  const runActivationNudges = async () => {
    try {
      const { data } = await api.post("/admin/run-activation-nudges?min_age_days=0");
      toast({ title: "Activation nudges run", description: data.reason ? data.reason : `Sent ${data.sent}, skipped ${data.skipped} (of ${data.candidates} un-activated).` });
    } catch (err) {
      toast({ title: "Error", description: "Could not run activation nudges", variant: "destructive" });
    }
  };

  const tabs = [
    { id: "businesses", label: "Businesses", icon: Building2 },
    { id: "revenue", label: "Revenue", icon: PoundSterling },
    ...(isFullAdmin ? [
      { id: "plans", label: "Plans", icon: Gem },
      { id: "platform", label: "Platform & Stripe", icon: CreditCard },
      { id: "content", label: "Site content", icon: FileText },
    ] : []),
    { id: "articles", label: "Articles", icon: Newspaper },
    { id: "activity", label: "Activity", icon: History },
    ...(isFullAdmin ? [{ id: "staff", label: "Staff", icon: Users }] : []),
  ];

  const totalSales = businesses ? businesses.reduce((s, b) => s + (b.total_sales || 0), 0) : 0;
  const totalVouchers = businesses ? businesses.reduce((s, b) => s + (b.voucher_count || 0), 0) : 0;

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <header className="bg-[#16243b] text-white">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-semibold">GiftVouchers.app <span className="text-[#2ec9c9]">Admin</span></span>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} className="flex items-center gap-2 text-sm text-[#9fb0c8] hover:text-white"><LogOut size={16} /> Sign out</button>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 py-8">
        {isFullAdmin && <WebhookAlert health={webhookHealth} onOpen={() => setTab("platform")} />}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Stat label="Businesses" value={businesses?.length ?? "–"} icon={Building2} />
          <Stat label="Total voucher sales" value={`£${totalSales.toFixed(2)}`} icon={PoundSterling} />
          <Stat label="Vouchers issued" value={totalVouchers} icon={Ticket} />
        </div>

        <div className="flex gap-2 mb-6">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${tab === t.id ? "bg-[#16243b] text-white" : "bg-white text-[#5a6b80]"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "businesses" && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <button onClick={runActivationNudges} data-testid="run-activation-nudges-btn" className="gp-btn-outline rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-2"><Zap size={15} /> Send activation nudges</button>
            </div>
            <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
            {!businesses ? <Loader2 className="animate-spin text-[#2ec9c9] m-8" /> : (
              <table className="w-full text-sm">
                <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Business", "Store URL", "Plan", "Vouchers", "Sales", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
              <tbody>
                {businesses.map((b) => (
                  <tr key={b.id} className={`border-t border-gray-50 ${b.archived ? "opacity-50" : ""}`}>
                    <td className="px-5 py-3 font-medium text-[#16243b]">{b.name} {b.archived && <span className="text-xs text-amber-600 ml-1">(archived)</span>}{b.suspended && <span className="ml-1 rounded-full bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 text-[11px]" title={b.suspend_reason || ""} data-testid={`suspended-badge-${b.id}`}>Suspended</span>}</td>
                    <td className="px-5 py-3"><a href={`/store/${b.slug}`} target="_blank" rel="noreferrer" className="text-[#1fb0b0] hover:underline">/store/{b.slug}</a></td>
                    <td className="px-5 py-3">
                      <select value={b.plan || "standard"} onChange={(e) => assignPlan(b, e.target.value)} data-testid={`biz-plan-${b.id}`}
                        className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white">
                        {planList.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        {!planList.some((p) => p.id === (b.plan || "standard")) && <option value={b.plan || "standard"}>{b.plan || "standard"}</option>}
                      </select>
                    </td>
                    <td className="px-5 py-3 text-[#5a6b80]">{b.voucher_count}</td>
                    <td className="px-5 py-3 text-[#16243b]">£{(b.total_sales || 0).toFixed(2)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex flex-wrap justify-end items-center gap-x-2 gap-y-1.5 min-w-[220px]">
                      <button onClick={() => loginAs(b.id)} data-testid={`login-as-${b.id}`} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1"><LogIn size={13} /> Log in as</button>
                      {isFullAdmin && <button onClick={() => setPwBiz(b)} data-testid={`reset-password-${b.id}`} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1 ml-1"><KeyRound size={13} /> Reset password</button>}
                      {isFullAdmin && <button onClick={() => toggleSuspend(b)} data-testid={`suspend-${b.id}`} className={`ml-2 text-xs font-medium hover:underline ${b.suspended ? "text-green-600" : "text-red-500"}`}>{b.suspended ? "Unsuspend" : "Suspend"}</button>}
                      <button onClick={() => archiveBiz(b)} data-testid={`archive-${b.id}`} className="ml-2 text-xs font-medium text-amber-600 hover:underline">{b.archived ? "Restore" : "Archive"}</button>
                      <button onClick={() => deleteBiz(b)} data-testid={`delete-${b.id}`} className="ml-2 text-red-400 hover:text-red-600"><Trash2 size={14} className="inline" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {businesses.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8a97a8]">No businesses yet.</td></tr>}
              </tbody>
              </table>
            )}
            </div>
          </div>
        )}

        {tab === "revenue" && (
          <div data-testid="revenue-tab">
            {!fees ? <Loader2 className="animate-spin text-[#2ec9c9] m-8" /> : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Stat label="Total commission (all plans)" value={`£${fees.total_commission.toFixed(2)}`} icon={PoundSterling} />
                  <Stat label="Gross voucher sales" value={`£${fees.total_gross_sales.toFixed(2)}`} icon={Ticket} />
                  <Stat label="Active subscriptions" value={fees.active_subscriptions} icon={CheckCircle2} />
                  <Stat label="Subscription MRR" value={`£${fees.estimated_mrr.toFixed(2)}`} icon={CreditCard} />
                </div>

                {payouts && payouts.some((r) => r.status === "requested") && (
                  <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden mb-6" data-testid="admin-payouts-panel">
                    <div className="p-4 border-b border-gray-50 flex items-center gap-2">
                      <PoundSterling className="text-[#2ec9c9]" size={18} />
                      <h3 className="font-semibold text-[#16243b]">Payout requests</h3>
                      <span className="text-xs text-[#8a97a8]">— funds you're holding for businesses. Mark paid after you transfer the money.</span>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Business", "Amount", "Requested", "Status", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                      <tbody>
                        {payouts.map((r) => (
                          <tr key={r.id} className="border-t border-gray-50" data-testid={`admin-payout-${r.id}`}>
                            <td className="px-5 py-3 font-medium text-[#16243b]">{r.business_name}</td>
                            <td className="px-5 py-3 text-[#16243b] font-semibold">£{r.amount.toFixed(2)}</td>
                            <td className="px-5 py-3 text-[#5a6b80]">{String(r.created_at).slice(0, 10)}</td>
                            <td className="px-5 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${r.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r.status === "paid" ? "Paid" : "Requested"}</span></td>
                            <td className="px-5 py-3 text-right">
                              {r.status === "requested" && <button onClick={() => markPayoutPaid(r)} data-testid={`mark-paid-${r.id}`} className="gp-btn-teal rounded-full px-3 py-1.5 text-xs font-medium">Mark paid</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
                  <div className="p-4 border-b border-gray-50">
                    <input value={revFilter} onChange={(e) => setRevFilter(e.target.value)} placeholder="Filter businesses by name…" data-testid="revenue-filter"
                      className="w-full max-w-sm rounded-xl border border-gray-200 px-4 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Business", "Gross sales", "Commission", "Stripe", "Monthly fee", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
                    <tbody>
                      {fees.businesses.filter((b) => b.name.toLowerCase().includes(revFilter.toLowerCase())).map((b) => (
                        <tr key={b.id} className="border-t border-gray-50" data-testid={`fee-row-${b.id}`}>
                          <td className="px-5 py-3 font-medium text-[#16243b]">{b.name}{b.referrals > 0 && <span className="block text-xs font-normal text-[#1fb0b0]" data-testid={`fee-referrals-${b.id}`}>{b.referrals} referral{b.referrals > 1 ? "s" : ""} · {b.effective_pct}% rate</span>}</td>
                          <td className="px-5 py-3 text-[#5a6b80]">£{b.gross.toFixed(2)}</td>
                          <td className="px-5 py-3 text-[#16243b] font-medium">£{b.commission.toFixed(2)}<span className="block text-xs font-normal text-[#8a97a8]" data-testid={`fee-plan-${b.id}`}>{b.plan_name} · {b.effective_pct}%{b.commission_fixed ? ` + ${Math.round(b.commission_fixed * 100)}p` : ""}</span></td>
                          <td className="px-5 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${b.stripe_connected ? "bg-green-100 text-green-700" : "bg-slate-100 text-[#8a97a8]"}`}>{b.stripe_connected ? "Connected" : "Not connected"}</span></td>
                          <td className="px-5 py-3">
                            <select value={b.subscription_status === "past_due" || b.subscription_status === "canceled" ? "inactive" : b.subscription_status} onChange={(e) => setSubStatus(b, e.target.value)} data-testid={`sub-status-${b.id}`}
                              className="rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white capitalize">
                              <option value="active">Active (paying)</option>
                              <option value="waived">Waived (free)</option>
                              <option value="invoiced">Invoiced manually</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </td>
                          <td className="px-5 py-3 text-right"><button onClick={() => deleteBiz(b)} data-testid={`revenue-delete-${b.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={14} className="inline" /></button></td>
                        </tr>
                      ))}
                      {fees.businesses.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8a97a8]">No data yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {pwBiz && <ResetPasswordDialog biz={pwBiz} onClose={() => setPwBiz(null)} />}
        {tab === "plans" && isFullAdmin && <PlansAdmin />}
        {tab === "activity" && <ActivityLog businesses={businesses || []} />}

        {tab === "platform" && platform && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[560px] space-y-4">
            <p className="text-sm text-[#5a6b80] bg-[#eefafa] rounded-xl px-4 py-3" data-testid="fees-moved-note">Monthly fees and commission are now set per package in the <button type="button" onClick={() => setTab("plans")} className="text-[#1fb0b0] font-medium underline">Plans</button> tab.</p>
            <div className="border-t border-gray-100 pt-4" data-testid="referral-settings">
              <h4 className="font-semibold text-[#16243b]">Business referral program</h4>
              <p className="text-sm text-[#8a97a8] mb-3">Businesses get a lower commission % for every active business they refer.</p>
              <div className="grid grid-cols-2 gap-4">
                <AdminField label="Discount per referral (% points)" type="number" value={platform.referral_discount_pct ?? 0.5} onChange={(v) => setPlatform({ ...platform, referral_discount_pct: parseFloat(v) || 0 })} />
                <AdminField label="Maximum discount (% points)" type="number" value={platform.referral_max_discount_pct ?? 2.5} onChange={(v) => setPlatform({ ...platform, referral_max_discount_pct: parseFloat(v) || 0 })} help="Set either to 0 to switch the program off." />
              </div>
            </div>
            <AdminField label="Platform Stripe publishable key" value={platform.stripe_pk} onChange={(v) => setPlatform({ ...platform, stripe_pk: v })} placeholder="pk_live_…" mono />
            <AdminField label="Platform Stripe secret key" value={platform.stripe_sk} onChange={(v) => setPlatform({ ...platform, stripe_sk: v })} placeholder="sk_live_…" mono type="password" />
            <div className="rounded-xl bg-[#f6f8fb] p-4 space-y-3" data-testid="webhook-settings">
              <div>
                <p className="text-sm font-medium text-[#16243b]">Stripe webhook URL</p>
                <p className="text-xs text-[#8a97a8] mb-1.5">In Stripe → Developers → Webhooks, add an endpoint with this URL (from your live site, not the preview). Select events: checkout.session.completed, account.updated, invoice.paid, invoice.payment_failed, customer.subscription.deleted.</p>
                <div className="flex gap-2">
                  <input readOnly value={`${window.location.origin}/api/stripe/webhook`} data-testid="webhook-url" className="flex-1 min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-mono" />
                  <button type="button" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/api/stripe/webhook`); toast({ title: "Webhook URL copied" }); }} data-testid="copy-webhook-url" className="gp-btn-outline rounded-xl px-3 text-sm font-medium">Copy</button>
                </div>
              </div>
              <WebhookHealthPanel health={webhookHealth} />
              <AdminField label="Webhook signing secret" value={platform.stripe_webhook_secret || ""} onChange={(v) => setPlatform({ ...platform, stripe_webhook_secret: v })} placeholder="whsec_…" mono type="password" help="From Stripe → Webhooks → your endpoint → 'Reveal signing secret'. Each endpoint has its own secret, and test & live mode differ — if you have several endpoints (e.g. one for Connected accounts), paste all their secrets separated by commas." />
            </div>
            <StripeStatusPanel />

            <div className="border-t border-gray-100 pt-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-[#16243b]">Email delivery (SMTP)</h4>
                  <p className="text-sm text-[#8a97a8]">Send voucher PDFs to recipients on purchase.</p>
                </div>
                <label className="flex items-center gap-2 text-sm text-[#16243b]">
                  <input type="checkbox" checked={!!platform.email_enabled} onChange={(e) => setPlatform({ ...platform, email_enabled: e.target.checked })} /> Enabled
                </label>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <AdminField label="SMTP host" value={platform.smtp_host} onChange={(v) => setPlatform({ ...platform, smtp_host: v })} placeholder="smtp.example.com" />
                <AdminField label="SMTP port" type="number" value={platform.smtp_port} onChange={(v) => setPlatform({ ...platform, smtp_port: parseInt(v) || 587 })} placeholder="587" />
              </div>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <AdminField label="SMTP username" value={platform.smtp_user} onChange={(v) => setPlatform({ ...platform, smtp_user: v })} />
                <AdminField label="SMTP password" type="password" value={platform.smtp_password} onChange={(v) => setPlatform({ ...platform, smtp_password: v })} />
              </div>
              <div className="mt-4">
                <AdminField label="From address" value={platform.smtp_from} onChange={(v) => setPlatform({ ...platform, smtp_from: v })} placeholder="vouchers@yourbrand.com" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#16243b] mt-4">
                <input type="checkbox" checked={platform.smtp_use_tls !== false} onChange={(e) => setPlatform({ ...platform, smtp_use_tls: e.target.checked })} /> Use STARTTLS (recommended for port 587)
              </label>
              <div className="border-t border-gray-100 pt-4 mt-4" data-testid="sms-settings">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-[#16243b]">SMS (Twilio)</h4>
                  <label className="flex items-center gap-2 text-sm text-[#16243b]">
                    <input type="checkbox" checked={!!platform.sms_enabled} onChange={(e) => setPlatform({ ...platform, sms_enabled: e.target.checked })} data-testid="sms-enabled" /> Enabled
                  </label>
                </div>
                <p className="text-xs text-[#8a97a8] mb-3">Texts voucher codes to recipients (or buyers) on plans with "SMS voucher codes". Find these in your Twilio Console.</p>
                <div className="grid grid-cols-2 gap-3">
                  <AdminField label="Account SID" value={platform.twilio_sid || ""} onChange={(v) => setPlatform({ ...platform, twilio_sid: v })} />
                  <AdminField label="Auth Token" type="password" value={platform.twilio_token || ""} onChange={(v) => setPlatform({ ...platform, twilio_token: v })} />
                  <AdminField label="Sender (number or name)" value={platform.twilio_from || ""} onChange={(v) => setPlatform({ ...platform, twilio_from: v })} help="E.g. +447700900123 or an alphanumeric ID like GiftVouch (max 11 chars)." />
                  <div>
                    <label className="block text-sm font-medium text-[#16243b] mb-1.5">Send test SMS to</label>
                    <div className="flex gap-2">
                      <input value={smsTo} onChange={(e) => setSmsTo(e.target.value)} placeholder="07700 900123" data-testid="sms-test-to" className="flex-1 min-w-0 rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-[#2ec9c9]" />
                      <button type="button" onClick={testSms} data-testid="sms-test-btn" className="gp-btn-outline rounded-xl px-3 text-sm font-medium">Test</button>
                    </div>
                    <p className="text-xs text-[#8a97a8] mt-1">Save settings first.</p>
                  </div>
                </div>
              </div>
              <button type="button" onClick={runReminders} data-testid="run-reminders-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium mt-4">
                Run expiry reminders now
              </button>
              <p className="text-xs text-[#8a97a8] mt-2">Sends reminder emails for vouchers expiring within 14 days (skips fully-redeemed ones). Runs automatically every 6 hours.</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <button type="button" onClick={runScheduledSends} data-testid="run-scheduled-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send scheduled vouchers now
                </button>
                <button type="button" onClick={runCartNudges} data-testid="run-cart-nudges-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send abandoned-cart nudges now
                </button>
                <button type="button" onClick={() => runJob("/admin/run-low-balance-reminders", "Low-balance reminders", (d) => d.reason || `Sent ${d.sent}, skipped ${d.skipped} (of ${d.candidates} low-balance).`)} data-testid="run-low-balance-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send low-balance reminders now
                </button>
                <button type="button" onClick={() => runJob("/admin/run-pending-cleanup", "Pending cleanup", (d) => `Removed ${d.deleted} abandoned checkout(s) older than 24h.`)} data-testid="run-pending-cleanup-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Clean up abandoned checkouts
                </button>
              </div>
              <p className="text-xs text-[#8a97a8] mt-2">Low-balance reminders email holders once when a part-used voucher drops to 25% or less (every 6 hours). Unpaid checkouts older than 24 hours are deleted automatically every 15 minutes.</p>
              <div className="flex flex-wrap items-center gap-4 mt-4 border-t border-gray-100 pt-4" data-testid="promo-reminders-settings">
                <label className="flex items-center gap-2 text-sm text-[#16243b]">
                  <input type="checkbox" checked={platform.promo_reminders_enabled !== false} onChange={(e) => setPlatform({ ...platform, promo_reminders_enabled: e.target.checked })} data-testid="promo-reminders-toggle" />
                  Seasonal promo reminders to businesses
                </label>
                <button type="button" onClick={() => runJob("/admin/run-promo-reminders", "Promo reminders", (d) => d.reason || (d.holidays.length ? `Sent ${d.sent}, skipped ${d.skipped} for ${d.holidays.join(", ")}.` : "No holidays in the next 14 days."))} data-testid="run-promo-reminders-btn" className="gp-btn-outline rounded-xl px-5 py-2.5 text-sm font-medium">
                  Send promo reminders now
                </button>
              </div>
              <p className="text-xs text-[#8a97a8] mt-2">Emails each business 14 days before Mother's Day, Father's Day, Valentine's, Easter, Black Friday and Christmas (once per holiday per year), unless they already run a promo or opted out.</p>
              <p className="text-xs text-[#8a97a8] mt-2">Scheduled vouchers are delivered on their chosen date; abandoned-cart nudges email buyers who started a Stripe checkout but didn't pay. Both run automatically every 15 minutes.</p>
            </div>

            <button onClick={savePlatform} className="gp-btn-teal rounded-xl px-6 py-3 font-medium">Save platform settings</button>
            <ChangePasswordCard />
          </div>
        )}

        {tab === "content" && content && (
          <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] max-w-[760px] space-y-6">
            <div>
              <h4 className="font-semibold text-[#16243b] mb-1">Home page text &amp; images</h4>
              <p className="text-sm text-[#8a97a8] mb-4">Edit every heading, paragraph, button label and image on the public landing page. Image fields take a URL.</p>
            </div>

            {CONTENT_GROUPS.map((g) => (
              <div key={g.title} className="border-t border-gray-100 pt-5">
                <h5 className="font-semibold text-[#16243b] text-sm mb-3">{g.title}</h5>
                <div className="space-y-3">
                  {g.keys.filter((k) => k in content).map((k) => (
                    <AdminField key={k} label={k === "favicon" ? "favicon (browser tab icon)" : k.replace(/_/g, " ")} value={content[k]} onChange={(v) => setContent({ ...content, [k]: v })} isImage={/image|avatar|logo|favicon/.test(k)}
                      square={k === "favicon"}
                      hint={k === "favicon" ? "Upload a square image (PNG, e.g. 512×512). Appears in the browser tab & bookmarks." : undefined} />
                  ))}
                </div>
              </div>
            ))}

            <DesignEditor content={content} setContent={setContent} />
            <SectionTogglesEditor content={content} setContent={setContent} />
            <DefaultVouchersEditor content={content} setContent={setContent} />
            <ImageListEditor title="Voucher store screenshots" listKey="store_slides" content={content} setContent={setContent} />
            <IntegrationLogosEditor content={content} setContent={setContent} />
            <CaseStudiesEditor content={content} setContent={setContent} />
            <HeaderLinksEditor content={content} setContent={setContent} />
            <FooterEditor content={content} setContent={setContent} />
            <SocialLinksEditor content={content} setContent={setContent} />
            <LegalEditor content={content} setContent={setContent} />

            <button onClick={saveContent} data-testid="save-content-btn" className="gp-btn-teal rounded-xl px-6 py-3 font-medium">Save content</button>
          </div>
        )}

        {tab === "articles" && (
          <ArticlesTab articles={articles} setArticles={setArticles} />
        )}

        {tab === "staff" && isFullAdmin && (
          <StaffTab currentUserId={user.id} />
        )}
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (nw.length < 8) { toast({ title: "Too short", description: "New password must be at least 8 characters", variant: "destructive" }); return; }
    if (nw !== cf) { toast({ title: "Passwords don't match", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast({ title: "Password changed", description: "Your password has been updated." });
      setCur(""); setNw(""); setCf("");
    } catch (err) {
      toast({ title: "Could not change password", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-gray-100 pt-5 space-y-3" data-testid="change-password-card">
      <h4 className="font-semibold text-[#16243b]">Change password</h4>
      <form onSubmit={submit} className="space-y-3 max-w-[420px]">
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required placeholder="Current password" data-testid="cp-current" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        <input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required placeholder="New password (min 8 chars)" data-testid="cp-new" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        <input type="password" value={cf} onChange={(e) => setCf(e.target.value)} required placeholder="Confirm new password" data-testid="cp-confirm" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
        <button disabled={busy} data-testid="cp-submit" className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium disabled:opacity-60">{busy ? "Saving…" : "Update password"}</button>
      </form>
    </div>
  );
}

function StaffTab({ currentUserId }) {
  const [staff, setStaff] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", staff_role: "business_manager" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/staff").then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  useEffect(() => { load(); }, []);

  const roleLabel = (r) => (r === "full_admin" ? "Full Admin" : "Business Manager");

  const addStaff = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast({ title: "Password too short", description: "At least 8 characters", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/admin/staff", form);
      toast({ title: "Staff member added", description: `${form.name} can now sign in.` });
      setForm({ name: "", email: "", password: "", staff_role: "business_manager" });
      setShowAdd(false);
      load();
    } catch (err) {
      toast({ title: "Could not add staff", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(false); }
  };

  const changeRole = async (s, newRole) => {
    try {
      await api.put(`/admin/staff/${s.id}`, { staff_role: newRole });
      toast({ title: "Role updated", description: `${s.name} is now ${roleLabel(newRole)}.` });
      load();
    } catch (err) {
      toast({ title: "Could not update role", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  const removeStaff = async (s) => {
    if (!window.confirm(`Remove ${s.name} (${s.email})? They will lose all access.`)) return;
    try {
      await api.delete(`/admin/staff/${s.id}`);
      toast({ title: "Staff member removed" });
      load();
    } catch (err) {
      toast({ title: "Could not remove", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6 max-w-[820px]" data-testid="staff-tab">
      <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex items-center gap-2 text-sm text-[#5a6b80]">
          <ShieldCheck className="text-[#2ec9c9]" size={18} />
          <p><strong className="text-[#16243b]">Full Admins</strong> can access everything. <strong className="text-[#16243b]">Business Managers</strong> can manage businesses, revenue and articles, but not Platform &amp; Stripe or Site content.</p>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-[#16243b]">Staff members</h3>
        <button onClick={() => setShowAdd((v) => !v)} data-testid="add-staff-toggle" className="gp-btn-teal rounded-full px-4 py-2 text-sm font-medium inline-flex items-center gap-1"><Plus size={15} /> Add staff</button>
      </div>

      {showAdd && (
        <form onSubmit={addStaff} className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)] grid sm:grid-cols-2 gap-4" data-testid="add-staff-form">
          <AdminField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <AdminField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="staff@yourbrand.com" />
          <AdminField label="Temporary password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} placeholder="Min 8 characters" />
          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Role</label>
            <select value={form.staff_role} onChange={(e) => setForm({ ...form, staff_role: e.target.value })} data-testid="staff-role-select"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
              <option value="business_manager">Business Manager</option>
              <option value="full_admin">Full Admin</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <button disabled={busy} data-testid="save-staff-btn" className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium disabled:opacity-60">{busy ? "Adding…" : "Add staff member"}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl shadow-[0_8px_24px_rgba(20,30,55,0.06)] overflow-hidden">
        {!staff ? <Loader2 className="animate-spin text-[#2ec9c9] m-8" /> : (
          <table className="w-full text-sm">
            <thead className="bg-[#f6f8fb] text-[#8a97a8]"><tr>{["Name", "Email", "Role", ""].map((h) => <th key={h} className="text-left px-5 py-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-t border-gray-50" data-testid={`staff-row-${s.id}`}>
                  <td className="px-5 py-3 font-medium text-[#16243b]">{s.name} {s.id === currentUserId && <span className="text-xs text-[#8a97a8]">(you)</span>}</td>
                  <td className="px-5 py-3 text-[#5a6b80]">{s.email}</td>
                  <td className="px-5 py-3">
                    <select value={s.staff_role} disabled={s.id === currentUserId} onChange={(e) => changeRole(s, e.target.value)} data-testid={`staff-role-${s.id}`}
                      className={`rounded-lg border border-gray-200 px-2 py-1 text-xs bg-white ${s.id === currentUserId ? "opacity-60 cursor-not-allowed" : ""}`}>
                      <option value="business_manager">Business Manager</option>
                      <option value="full_admin">Full Admin</option>
                    </select>
                  </td>
                  <td className="px-5 py-3 text-right">
                    {s.id !== currentUserId && (
                      <button onClick={() => removeStaff(s)} data-testid={`staff-delete-${s.id}`} className="text-red-400 hover:text-red-600"><Trash2 size={14} className="inline" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {staff.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-[#8a97a8]">No staff yet.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
      <div className="w-10 h-10 rounded-xl bg-[#eefafa] flex items-center justify-center mb-3"><Icon className="text-[#2ec9c9]" size={20} /></div>
      <p className="text-[#8a97a8] text-sm">{label}</p>
      <p className="text-2xl font-bold text-[#16243b] mt-1">{value}</p>
    </div>
  );
}

function AdminField({ label, value, onChange, type = "text", placeholder, mono, isImage, help, square, hint }) {
  if (isImage) {
    return <ImageUpload label={label} value={value} onChange={onChange} square={square} hint={hint} />;
  }
  return (
    <div>
      <label className="block text-sm font-medium text-[#16243b] mb-1.5 capitalize">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className={`w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 ${mono ? "font-mono text-sm" : ""}`} />
      {help && <p className="text-xs text-[#8a97a8] mt-1">{help}</p>}
    </div>
  );
}

function IntegrationLogosEditor({ content, setContent }) {
  const raw = content.integration_logos || [];
  const list = raw.map((it) => (typeof it === "string" ? { image: it, url: "" } : it));
  const update = (next) => setContent({ ...content, integration_logos: next });
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-[#16243b] text-sm">Integration logos <span className="text-xs font-normal text-[#8a97a8]">(each links to a URL)</span></h5>
        <button type="button" onClick={() => update([...list, { image: "", url: "" }])} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add logo</button>
      </div>
      <div className="space-y-3">
        {list.map((it, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`integration-row-${i}`}>
            <div className="flex-1 space-y-2">
              <ImageUpload value={it.image} compact onChange={(v) => update(list.map((x, xi) => (xi === i ? { ...x, image: v } : x)))} />
              <input value={it.url || ""} placeholder="Link URL (https://…)" data-testid={`integration-url-${i}`} onChange={(e) => update(list.map((x, xi) => (xi === i ? { ...x, url: e.target.value } : x)))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
            </div>
            <button type="button" data-testid={`integration-remove-${i}`} onClick={() => update(list.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50"><Trash2 size={16} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#8a97a8]">No logos. Add one above.</p>}
      </div>
    </div>
  );
}

const HOME_SECTIONS = [
  ["logos", "Customer logo strip"],
  ["stores", "Voucher stores"],
  ["video", "Video messages"],
  ["admin", "Admin tools"],
  ["fulfil", "Fulfilment"],
  ["support", "Support"],
  ["integrations", "Integrations"],
  ["case_studies", "Case studies / clients"],
  ["pricing", "Pricing / plans"],
  ["grow", "Grow CTA / pricing"],
  ["blog", "Blog / articles"],
];

function SectionTogglesEditor({ content, setContent }) {
  const sections = content.sections || {};
  const isOn = (k) => sections[k] !== false;
  const toggle = (k) => setContent({ ...content, sections: { ...sections, [k]: !isOn(k) } });
  const labels = Object.fromEntries(HOME_SECTIONS);
  const saved = (content.section_order || []).filter((k) => labels[k]);
  const order = [...saved, ...HOME_SECTIONS.map(([k]) => k).filter((k) => !saved.includes(k))];
  const [drag, setDrag] = React.useState(null);
  const move = (from, to) => {
    if (to < 0 || to >= order.length || from === to) return;
    setContent({ ...content, section_order: reorder(order, from, to) });
  };
  return (
    <div className="border-t border-gray-100 pt-5" data-testid="section-toggles-editor">
      <h5 className="font-semibold text-[#16243b] text-sm mb-1">Home page sections <span className="text-xs font-normal text-[#8a97a8]">(drag to reorder)</span></h5>
      <p className="text-xs text-[#8a97a8] mb-3">Drag sections into the order you want and switch each on or off. The header, hero and footer always stay in place.</p>
      <div className="space-y-2" data-testid="section-order-list">
        {order.map((k, i) => (
          <div key={k} draggable data-testid={`section-row-${k}`}
            onDragStart={() => setDrag(i)} onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (drag !== null) move(drag, i); setDrag(null); }} onDragEnd={() => setDrag(null)}
            className={`flex items-center gap-3 rounded-xl border px-3 py-2 text-sm bg-white transition-opacity ${drag === i ? "opacity-40" : ""} ${isOn(k) ? "border-[#2ec9c9]/60" : "border-gray-200"}`}>
            <span className="cursor-grab text-[#c0c8d2]" title="Drag to reorder"><GripVertical size={16} /></span>
            <span className="text-xs text-[#8a97a8] w-5">{i + 1}</span>
            <span className={`flex-1 font-medium ${isOn(k) ? "text-[#16243b]" : "text-[#8a97a8] line-through"}`}>{labels[k]}</span>
            <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} data-testid={`section-up-${k}`} className="px-1.5 text-[#5a6b80] hover:text-[#16243b] disabled:opacity-25" aria-label="Move up"><ChevronUp size={16} /></button>
            <button type="button" onClick={() => move(i, i + 1)} disabled={i === order.length - 1} data-testid={`section-down-${k}`} className="px-1.5 text-[#5a6b80] hover:text-[#16243b] disabled:opacity-25" aria-label="Move down"><ChevronDown size={16} /></button>
            <button type="button" onClick={() => toggle(k)} data-testid={`section-toggle-${k}`} aria-label="Toggle section"
              className={`inline-flex h-5 w-9 items-center rounded-full transition-colors ${isOn(k) ? "bg-[#2ec9c9]" : "bg-gray-300"}`}>
              <span className={`h-4 w-4 rounded-full bg-white transition-transform ${isOn(k) ? "translate-x-4" : "translate-x-0.5"}`} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DefaultVouchersEditor({ content, setContent }) {
  const list = content.default_vouchers || [];
  const update = (next) => setContent({ ...content, default_vouchers: next });
  return (
    <div className="border-t border-gray-100 pt-5" data-testid="default-vouchers-editor">
      <div className="flex items-center justify-between mb-1">
        <h5 className="font-semibold text-[#16243b] text-sm">Starter voucher templates</h5>
        <button type="button" onClick={() => update([...list, { amount: 0, image: "" }])} data-testid="add-default-voucher" className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add amount</button>
      </div>
      <p className="text-xs text-[#8a97a8] mb-3">These voucher amounts (and images) are auto-added to every new store to help businesses launch faster. Businesses can change or remove them anytime.</p>
      <div className="space-y-3">
        {list.map((it, i) => (
          <div key={i} className="flex items-start gap-3" data-testid={`default-voucher-row-${i}`}>
            <div className="w-28 shrink-0">
              <label className="block text-xs text-[#8a97a8] mb-1">Amount (£)</label>
              <input type="number" value={it.amount} data-testid={`default-voucher-amount-${i}`} onChange={(e) => update(list.map((x, xi) => (xi === i ? { ...x, amount: parseFloat(e.target.value) || 0 } : x)))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-[#8a97a8] mb-1">Image (square)</label>
              <ImageUpload value={it.image} compact square onChange={(v) => update(list.map((x, xi) => (xi === i ? { ...x, image: v } : x)))} />
            </div>
            <button type="button" data-testid={`default-voucher-remove-${i}`} onClick={() => update(list.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 mt-5"><Trash2 size={16} /></button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#8a97a8]">No starter vouchers. Add one above.</p>}
      </div>
    </div>
  );
}


function ImageListEditor({ title, listKey, content, setContent }) {
  const list = content[listKey] || [];
  const update = (next) => setContent({ ...content, [listKey]: next });
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-[#16243b] text-sm">{title}</h5>
        <button type="button" onClick={() => update([...list, ""])} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add image</button>
      </div>
      <div className="space-y-2">
        {list.map((src, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`${listKey}-row-${i}`}>
            <div className="flex-1">
              <ImageUpload value={src} compact
                onChange={(v) => update(list.map((x, xi) => (xi === i ? v : x)))} />
            </div>
            <button type="button" data-testid={`${listKey}-remove-${i}`} title="Remove"
              onClick={() => update(list.filter((_, xi) => xi !== i))}
              className="text-red-400 hover:text-red-600 shrink-0 p-2 rounded-lg hover:bg-red-50">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-[#8a97a8]">No images. Add one above.</p>}
      </div>
    </div>
  );
}

function CaseStudiesEditor({ content, setContent }) {
  const list = content.case_studies || [];
  const update = (next) => setContent({ ...content, case_studies: next });
  const setItem = (i, item) => update(list.map((x, xi) => (xi === i ? item : x)));
  const field = (i, key, ph) => (
    <input value={list[i][key] || ""} onChange={(e) => setItem(i, { ...list[i], [key]: e.target.value })} placeholder={ph}
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
  );
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-[#16243b] text-sm">Client case studies</h5>
        <button type="button" onClick={() => update([...list, { image: "", stat: "", statLabel: "", quote: "", name: "", role: "" }])} className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add</button>
      </div>
      <div className="space-y-4">
        {list.map((c, i) => (
          <div key={i} className="bg-[#f6f8fb] rounded-xl p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-[#8a97a8]">Case study {i + 1}</span>
              <button type="button" onClick={() => update(list.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
            {field(i, "image", "Image URL")}
            <div className="grid grid-cols-2 gap-2">{field(i, "stat", "Stat e.g. 35%")}{field(i, "statLabel", "Stat label")}</div>
            {field(i, "quote", "Quote")}
            <div className="grid grid-cols-2 gap-2">{field(i, "name", "Name")}{field(i, "role", "Role")}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticlesTab({ articles, setArticles }) {
  const [modal, setModal] = React.useState(null);
  const [drag, setDrag] = React.useState(null);
  const load = () => api.get("/articles").then(({ data }) => setArticles(data));
  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal.id) await api.put(`/admin/articles/${modal.id}`, modal);
      else await api.post("/admin/articles", modal);
      toast({ title: "Saved", description: "Article saved." });
      setModal(null); load();
    } catch (err) { toast({ title: "Error", description: "Failed to save", variant: "destructive" }); }
  };
  const del = async (id) => { await api.delete(`/admin/articles/${id}`); toast({ title: "Deleted" }); load(); };
  const onDrop = async (i) => {
    if (drag === null || drag === i) { setDrag(null); return; }
    const next = reorder(articles, drag, i);
    setArticles(next); setDrag(null);
    await api.post("/admin/articles/reorder", { ids: next.map((a) => a.id) });
    toast({ title: "Order saved" });
  };
  if (!articles) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#8a97a8]">Drag cards to reorder how they appear on the home page.</p>
        <button onClick={() => setModal({ tag: "Articles", image: "", title: "", excerpt: "" })} className="gp-btn-teal rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2"><Plus size={18} /> New article</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {articles.map((a, i) => (
          <div key={a.id}
            draggable
            onDragStart={() => setDrag(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            className={`bg-white rounded-2xl overflow-hidden shadow-[0_8px_24px_rgba(20,30,55,0.06)] ${drag === i ? "opacity-50" : ""}`}>
            <div className="h-36 bg-gray-100 relative">
              {a.image && <img src={a.image} alt={a.title} className="w-full h-full object-cover" />}
              <span className="absolute top-2 left-2 bg-white/90 rounded-md p-1 text-[#5a6b80] cursor-grab"><GripVertical size={14} /></span>
            </div>
            <div className="p-4">
              <span className="text-[#2ec9c9] text-xs font-semibold uppercase">{a.tag}</span>
              <h3 className="font-semibold text-[#16243b] mt-1 line-clamp-2">{a.title}</h3>
              <p className="text-sm text-[#8a97a8] mt-1 line-clamp-2">{a.excerpt}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => setModal(a)} className="flex-1 gp-btn-outline rounded-lg py-2 text-sm font-medium">Edit</button>
                <button onClick={() => del(a.id)} className="px-3 rounded-lg border border-red-200 text-red-500 hover:bg-red-50"><Trash2 size={16} /></button>
              </div>
            </div>
          </div>
        ))}
        {articles.length === 0 && <p className="text-[#8a97a8]">No articles yet.</p>}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-[460px]" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 font-bold text-[#16243b]">{modal.id ? "Edit article" : "New article"}</div>
            <form onSubmit={save} className="p-5 space-y-3">
              <input value={modal.title} onChange={(e) => setModal({ ...modal, title: e.target.value })} required placeholder="Title" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <input value={modal.tag} onChange={(e) => setModal({ ...modal, tag: e.target.value })} placeholder="Tag" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
              <ImageUpload label="Image" value={modal.image} onChange={(v) => setModal({ ...modal, image: v })} />
              <textarea value={modal.excerpt} onChange={(e) => setModal({ ...modal, excerpt: e.target.value })} rows={3} placeholder="Excerpt" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] resize-none" />
              <div className="flex gap-2">
                <button type="button" onClick={() => setModal(null)} className="flex-1 gp-btn-outline rounded-xl py-3 font-medium">Cancel</button>
                <button className="flex-1 gp-btn-teal rounded-xl py-3 font-medium">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function LinkRow({ link, onChange, onRemove, dragProps }) {
  return (
    <div className="flex gap-2 items-center bg-white" {...(dragProps || {})}>
      <span className="cursor-grab text-[#c0c8d2] shrink-0" title="Drag to reorder"><GripVertical size={16} /></span>
      <input value={link.label} onChange={(e) => onChange({ ...link, label: e.target.value })} placeholder="Label"
        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
      <input value={link.url} onChange={(e) => onChange({ ...link, url: e.target.value })} placeholder="URL (e.g. #pricing)"
        className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
      <button type="button" onClick={onRemove} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
    </div>
  );
}

function reorder(list, from, to) {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

function HeaderLinksEditor({ content, setContent }) {
  const links = content.header_links || [];
  const update = (next) => setContent({ ...content, header_links: next });
  const [drag, setDrag] = React.useState(null);
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#16243b]">Header menu <span className="text-xs font-normal text-[#8a97a8]">(drag to reorder)</span></h4>
        <button type="button" onClick={() => update([...links, { label: "New link", url: "#" }])}
          className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add link</button>
      </div>
      <div className="space-y-2">
        {links.map((l, i) => (
          <LinkRow key={i} link={l}
            dragProps={{
              draggable: true,
              onDragStart: () => setDrag(i),
              onDragOver: (e) => e.preventDefault(),
              onDrop: () => { if (drag !== null && drag !== i) update(reorder(links, drag, i)); setDrag(null); },
              className: `flex gap-2 items-center bg-white rounded-lg ${drag === i ? "opacity-50" : ""}`,
            }}
            onChange={(nl) => update(links.map((x, xi) => (xi === i ? nl : x)))}
            onRemove={() => update(links.filter((_, xi) => xi !== i))} />
        ))}
        {links.length === 0 && <p className="text-sm text-[#8a97a8]">No menu links. Add one above.</p>}
      </div>
    </div>
  );
}

const SOCIAL_PLATFORMS = ["linkedin", "twitter", "facebook", "instagram", "youtube"];

function ColorField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-3">
      <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
        data-testid={`color-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="w-10 h-10 rounded-lg border border-gray-200 cursor-pointer shrink-0 bg-white" />
      <div className="flex-1">
        <label className="block text-xs font-medium text-[#16243b] mb-0.5">{label}</label>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="#000000"
          className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm outline-none focus:border-[#2ec9c9]" />
      </div>
    </div>
  );
}

function DesignEditor({ content, setContent }) {
  const set = (k) => (v) => setContent({ ...content, [k]: v });
  return (
    <div className="border-t border-gray-100 pt-5" data-testid="design-editor">
      <h5 className="font-semibold text-[#16243b] text-sm mb-1">Design system</h5>
      <p className="text-xs text-[#8a97a8] mb-3">Control the site-wide colours and page background. Applies to buttons, links, headings and accents.</p>
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <ColorField label="Primary colour" value={content.primary_color} onChange={set("primary_color")} />
        <ColorField label="Accent colour" value={content.accent_color} onChange={set("accent_color")} />
        <ColorField label="Footer background" value={content.footer_bg_color} onChange={set("footer_bg_color")} />
      </div>
      <ImageUpload label="Page background image (optional)" value={content.background_image} onChange={set("background_image")} />
    </div>
  );
}

const LEGAL_PAGES = [
  { key: "legal_privacy", name: "Privacy policy" },
  { key: "legal_terms", name: "Terms & conditions" },
  { key: "legal_cookies", name: "Cookie policy" },
  { key: "legal_contact", name: "Contact" },
];

function LegalEditor({ content, setContent }) {
  const setItem = (key, patch) => setContent({ ...content, [key]: { ...(content[key] || {}), ...patch } });
  return (
    <div className="border-t border-gray-100 pt-5" data-testid="legal-editor">
      <h5 className="font-semibold text-[#16243b] text-sm mb-1">Legal &amp; contact pages</h5>
      <p className="text-xs text-[#8a97a8] mb-3">These open as pop-ups from the footer. Edit the title and content of each.</p>
      <div className="space-y-4">
        {LEGAL_PAGES.map(({ key, name }) => {
          const item = content[key] || { title: "", body: "" };
          return (
            <div key={key} className="bg-[#f6f8fb] rounded-xl p-4 space-y-2" data-testid={`legal-item-${key}`}>
              <p className="text-xs font-semibold text-[#8a97a8] uppercase tracking-wide">{name}</p>
              <input value={item.title || ""} onChange={(e) => setItem(key, { title: e.target.value })} placeholder="Title"
                data-testid={`legal-title-${key}`}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
              <textarea value={item.body || ""} onChange={(e) => setItem(key, { body: e.target.value })} rows={6} placeholder="Content"
                data-testid={`legal-body-${key}`}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] resize-y" />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SocialLinksEditor({ content, setContent }) {
  const links = content.social_links || [];
  const update = (next) => setContent({ ...content, social_links: next });
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#16243b]">Social media icons <span className="text-xs font-normal text-[#8a97a8]">(shown in the footer)</span></h4>
        <button type="button" data-testid="add-social-btn" onClick={() => update([...links, { platform: "linkedin", url: "" }])}
          className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add icon</button>
      </div>
      <div className="space-y-2">
        {links.map((s, i) => (
          <div key={i} className="flex gap-2 items-center" data-testid={`social-row-${i}`}>
            <select value={s.platform || "linkedin"} data-testid={`social-platform-${i}`}
              onChange={(e) => update(links.map((x, xi) => (xi === i ? { ...x, platform: e.target.value } : x)))}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9] capitalize bg-white">
              {SOCIAL_PLATFORMS.map((p) => <option key={p} value={p} className="capitalize">{p}</option>)}
            </select>
            <input value={s.url || ""} placeholder="https://…" data-testid={`social-url-${i}`}
              onChange={(e) => update(links.map((x, xi) => (xi === i ? { ...x, url: e.target.value } : x)))}
              className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
            <button type="button" onClick={() => update(links.filter((_, xi) => xi !== i))} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
          </div>
        ))}
        {links.length === 0 && <p className="text-sm text-[#8a97a8]">No social icons. Add one above.</p>}
      </div>
    </div>
  );
}

function FooterEditor({ content, setContent }) {
  const cols = content.footer_columns || [];
  const update = (next) => setContent({ ...content, footer_columns: next });
  const setCol = (ci, col) => update(cols.map((c, i) => (i === ci ? col : c)));
  return (
    <div className="border-t border-gray-100 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-[#16243b]">Footer menu</h4>
        <button type="button" onClick={() => update([...cols, { title: "New column", links: [] }])}
          className="gp-btn-outline rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1"><Plus size={14} /> Add column</button>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {cols.map((col, ci) => (
          <div key={ci} className="bg-[#f6f8fb] rounded-xl p-4">
            <div className="flex gap-2 items-center mb-3">
              <input value={col.title} onChange={(e) => setCol(ci, { ...col, title: e.target.value })} placeholder="Column title"
                className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium outline-none focus:border-[#2ec9c9]" />
              <button type="button" onClick={() => update(cols.filter((_, i) => i !== ci))} className="text-red-400 hover:text-red-600"><Trash2 size={16} /></button>
            </div>
            <div className="space-y-2">
              {(col.links || []).map((l, li) => (
                <LinkRow key={li} link={l}
                  onChange={(nl) => setCol(ci, { ...col, links: col.links.map((x, xi) => (xi === li ? nl : x)) })}
                  onRemove={() => setCol(ci, { ...col, links: col.links.filter((_, xi) => xi !== li) })} />
              ))}
            </div>
            <button type="button" onClick={() => setCol(ci, { ...col, links: [...(col.links || []), { label: "New link", url: "#" }] })}
              className="text-[#1fb0b0] text-xs font-medium mt-2 flex items-center gap-1"><Plus size={13} /> Add link</button>
          </div>
        ))}
      </div>
    </div>
  );
}
